import { z } from "zod";
import type { NudgeType } from "./NudgeType.js";


export const SerializedNudgeDataSchema = z.object({
    timesShown: z.number(),
    timesAccepted: z.number(),
    timesDenied: z.number(),
    lastShownDate: z.string().datetime().nullable(),
    lastConsideredDate: z.string().datetime().nullable(),
    skipsSinceLastShown: z.number(),
    consecutiveAccepts: z.number(),
    consecutiveDeclines: z.number(),
    customData: z.record(z.unknown()),
});
export type SerializedNudgeData = z.infer<typeof SerializedNudgeDataSchema>;


/**
 * Stores metadata about a nudge shown to the user.
 * Changes are persisted through the provided save function.
 */
export class NudgeData {
    private readonly nudgeType: NudgeType;
    private readonly saveFn: () => void;
    private timesShown: number;
    private timesAccepted: number;
    private timesDenied: number;
    private lastShownDate: Date | null;
    private lastConsideredDate: Date | null;
    private skipsSinceLastShown: number;
    private consecutiveAccepts: number;
    private consecutiveDeclines: number;
    private readonly customData: Record<string, unknown>;

    constructor(
        nudgeType: NudgeType,
        saveFn: () => void,
        timesShown: number,
        timesAccepted: number,
        timesDenied: number,
        lastShownDate: Date | null,
        lastConsideredDate: Date | null,
        skipsSinceLastShown: number,
        consecutiveAccepts: number,
        consecutiveDeclines: number,
        customData: Record<string, unknown>,
    ) {
        this.nudgeType = nudgeType;
        this.saveFn = saveFn;
        this.timesShown = timesShown;
        this.timesAccepted = timesAccepted;
        this.timesDenied = timesDenied;
        this.lastShownDate = lastShownDate;
        this.lastConsideredDate = lastConsideredDate;
        this.skipsSinceLastShown = skipsSinceLastShown;
        this.consecutiveAccepts = consecutiveAccepts;
        this.consecutiveDeclines = consecutiveDeclines;
        this.customData = customData;
    }

    getNudgeType(): NudgeType {
        return this.nudgeType;
    }

    getTimesShown(): number {
        return this.timesShown;
    }

    getTimesAccepted(): number {
        return this.timesAccepted;
    }

    getTimesDenied(): number {
        return this.timesDenied;
    }

    getLastShownDate(): Date | null {
        return this.lastShownDate;
    }

    getTimeSinceLastShownMs(): number {
        if (this.lastShownDate === null)
            return Infinity;
        return Date.now() - this.lastShownDate.getTime();
    }

    getLastConsideredDate(): Date | null {
        return this.lastConsideredDate;
    }

    getTimeSinceLastConsideredMs(): number {
        if (this.lastConsideredDate === null)
            return Infinity;
        return Date.now() - this.lastConsideredDate.getTime();
    }

    getSkipsSinceLastShown(): number {
        return this.skipsSinceLastShown;
    }

    getConsecutiveAccepts(): number {
        return this.consecutiveAccepts;
    }

    getConsecutiveDeclines(): number {
        return this.consecutiveDeclines;
    }

    getCustomData(key: string): unknown {
        return this.customData[key];
    }

    markShown(): void {
        this.skipsSinceLastShown = 0;
        this.timesShown++;
        const now = new Date();
        this.lastShownDate = now;
        this.lastConsideredDate = now;
        this.saveFn();
    }

    markAccepted(): void {
        this.consecutiveAccepts++;
        this.consecutiveDeclines = 0;
        this.timesAccepted++;
        this.saveFn();
    }

    markDismissed(): void {
        this.consecutiveDeclines++;
        this.consecutiveAccepts = 0;
        this.timesDenied++;
        this.saveFn();
    }

    markSkipped(): void {
        this.skipsSinceLastShown++;
        this.lastConsideredDate = new Date();
        this.saveFn();
    }

    setCustomData(key: string, value: unknown): void {
        this.customData[key] = value;
        this.saveFn();
    }

    serialize(): SerializedNudgeData {
        return {
            timesShown: this.timesShown,
            timesAccepted: this.timesAccepted,
            timesDenied: this.timesDenied,
            lastShownDate: this.lastShownDate ? this.lastShownDate.toISOString() : null,
            lastConsideredDate: this.lastConsideredDate ? this.lastConsideredDate.toISOString() : null,
            skipsSinceLastShown: this.skipsSinceLastShown,
            consecutiveAccepts: this.consecutiveAccepts,
            consecutiveDeclines: this.consecutiveDeclines,
            customData: this.customData,
        };
    }

    static createEmpty(nudgeType: NudgeType, saveFn: () => void): NudgeData {
        return new NudgeData(nudgeType, saveFn, 0, 0, 0, null, null, 0, 0, 0, {});
    }

    static deserialize(
        nudgeType: NudgeType,
        saveFn: () => void,
        data: unknown,
    ): NudgeData {
        const parseResult = SerializedNudgeDataSchema.safeParse(data);
        if (!parseResult.success) {
            const errorDetails = parseResult.error.errors
                .map(err => `${err.path.join(".")}: ${err.message}`)
                .join("; ");
            throw new Error(`Invalid nudge data: ${errorDetails}`);
        }

        const validData = parseResult.data;
        return new NudgeData(
            nudgeType,
            saveFn,
            validData.timesShown,
            validData.timesAccepted,
            validData.timesDenied,
            validData.lastShownDate ? new Date(validData.lastShownDate) : null,
            validData.lastConsideredDate ? new Date(validData.lastConsideredDate) : null,
            validData.skipsSinceLastShown,
            validData.consecutiveAccepts,
            validData.consecutiveDeclines,
            validData.customData,
        );
    }
}
